/**
 * Parses an ISO 8601 duration string (e.g., PT85H30M15S) into components
 */
export function parseISO8601Duration(duration: string): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
} {
  const result = { days: 0, hours: 0, minutes: 0, seconds: 0 };

  if (!duration || !duration.startsWith('PT')) {
    return result;
  }

  const timeString = duration.slice(2); // Remove "PT"

  const hoursMatch = timeString.match(/(\d+)H/);
  const minutesMatch = timeString.match(/(\d+)M/);
  const secondsMatch = timeString.match(/(\d+)S/);

  const totalHours = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
  result.minutes = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
  result.seconds = secondsMatch ? parseInt(secondsMatch[1], 10) : 0;

  // Convert hours to days + remaining hours
  result.days = Math.floor(totalHours / 24);
  result.hours = totalHours % 24;

  return result;
}

/**
 * Formats an ISO 8601 duration string into a human-readable format
 *
 * @example
 * formatDuration("PT85H30M") // "3d 13h 30m"
 * formatDuration("PT2H") // "2h"
 * formatDuration("PT45M") // "45m"
 * formatDuration("PT0S") // "0m"
 */
export function formatDuration(isoDuration: string): string {
  const { days, hours, minutes } = parseISO8601Duration(isoDuration);

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${String(days)}d`);
  }

  if (hours > 0) {
    parts.push(`${String(hours)}h`);
  }

  if (minutes > 0 || parts.length === 0) {
    parts.push(`${String(minutes)}m`);
  }

  return parts.join(' ');
}

/**
 * Formats a duration into a human-readable format showing only hours or minutes (if no hours).
 * Accepts either an ISO 8601 duration string or milliseconds.
 *
 * @param duration - ISO 8601 duration string (e.g. "PT2H30M") or milliseconds
 *
 * @example
 * // ISO 8601 duration string
 * formatDurationHoursOrMinutes("PT85H30M") // "85h"
 * formatDurationHoursOrMinutes("PT2H") // "2h"
 * formatDurationHoursOrMinutes("PT45M") // "45m"
 *
 * @example
 * // Milliseconds
 * formatDurationHoursOrMinutes(3600000) // "1h"
 * formatDurationHoursOrMinutes(5400000) // "1h" (90 min)
 * formatDurationHoursOrMinutes(2700000) // "45m"
 */
export function formatDurationHoursOrMinutes(
  duration: string | number,
): string {
  let totalHours: number;
  let totalMinutes: number;

  if (typeof duration === 'number') {
    if (duration <= 0) {
      return '0m';
    }
    totalMinutes = Math.floor(duration / (1000 * 60));
    totalHours = Math.floor(totalMinutes / 60);
  } else {
    const { days, hours, minutes } = parseISO8601Duration(duration);
    totalHours = days * 24 + hours;
    totalMinutes = minutes;
  }

  if (totalHours > 0) {
    return `${String(totalHours)}h`;
  }

  return `${String(totalMinutes)}m`;
}
